import React, { useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import Select from "react-select";
import { APIContext } from "../../contexts/APIContext";
import { GraphicsContext } from "../../contexts/GraphicsContext";
import useLocalStorage from "../../Hooks/useLocalStorage";
import { ScrollBar } from "../../Theme/Hyper";
import {
  Panel,
  PanelTitle,
  SubTitle,
  Controls,
  Field,
  Label,
  Button,
  Empty,
} from "./Styles";
import {
  TREE_ROUNDS,
  analyseKnockout,
  buildTree,
  resolveTree,
  slotKey,
} from "./buildTournamentTree";

// A tree can span most of a season (round of 16 in the autumn or winter,
// final in the spring), so fetch wide and keep only this game's season.
const TREE_WINDOW_DAYS = 240;

const Tree = styled(ScrollBar)`
  display: flex;
  overflow-x: auto;
  margin-top: 1em;
  padding-bottom: 0.5em;
`;

const RoundColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 260px;
  margin-right: 1em;
`;

const RoundTitle = styled.div`
  display: flex;
  font-size: 0.8em;
  opacity: 0.6;
  margin-bottom: 0.5em;
`;

// Spreading each column's ties evenly over the tallest column's height puts
// later ties roughly level with the pair that feeds them.
const Ties = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  flex: 1;
`;

const TieCard = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #202731;
  border-radius: 4px;
  border-left: 3px solid
    ${(props) => (props.edited ? "#db0a41" : "transparent")};
  padding: 0.3em;
  margin: 0.3em 0;
`;

const TeamRow = styled.div`
  display: flex;
  align-items: center;
  margin: 0.1em 0;
`;

const TeamSelect = styled(Select)`
  flex: 1;
  min-width: 0;
  font-size: 0.8em;
`;

const RoundSelect = styled(Select)`
  width: 170px;
  font-size: 0.875rem;
`;

const Score = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
  min-width: 55px;
  padding-left: 0.5em;
  font-size: 0.8em;
  white-space: nowrap;
  font-weight: ${(props) => (props.winner ? "500" : "100")};
`;

const ResetSlot = styled.div`
  display: flex;
  align-self: flex-end;
  font-size: 0.7em;
  opacity: 0.6;
  cursor: pointer;
  :hover {
    opacity: 1;
    color: #db0a41;
  }
`;

// The tree scrolls sideways, so menus render into the page body rather than
// being clipped by it.
const DropdownStyles = {
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#0e1219",
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  option: (provided) => ({
    ...provided,
    backgroundColor: "#0e1219",
    "&:hover": {
      color: "#db0a41",
    },
  }),
  control: (provided) => ({
    ...provided,
    minHeight: "30px",
    backgroundColor: "#0e1219",
    borderColor: "#384454",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 6px",
    backgroundColor: "#0e1219",
  }),
  indicatorsContainer: (provided) => ({ ...provided, height: "28px" }),
  input: (provided) => ({ ...provided, margin: 0, color: "#fff" }),
  singleValue: (provided) => ({ ...provided, color: "#fff" }),
};

function scoreText(tie, n) {
  if (!tie?.started) return "–";
  let pens = tie["team" + n + "Pens"];
  return tie["team" + n + "Score"] + (pens !== null ? ` (${pens}p)` : "");
}

export default function TournamentTreePanel({ game }) {
  const { getFixturesByDateRange } = useContext(APIContext);
  const { previewGraphic } = useContext(GraphicsContext);

  const [fixtures, setFixtures] = useState();
  // Saved per season: which round the tree starts at, and any slots the
  // operator has corrected, keyed like the scene's fields (QuarterFinal_1).
  const [settings, setSettings] = useLocalStorage(
    "hyper_football_tournament_tree_" + game?.season?._id,
    { startRound: TREE_ROUNDS[0].key, overrides: {} },
  );
  let overrides = settings?.overrides || {};
  let startIndex = Math.max(
    0,
    TREE_ROUNDS.findIndex((r) => r.key === settings?.startRound),
  );

  async function loadFixtures() {
    let day = moment(game?.date, "YYYY-MM-DD");
    let results = await getFixturesByDateRange({
      start_date:
        day.clone().subtract(TREE_WINDOW_DAYS, "days").format("YYYY-MM-DD") +
        "Z",
      end_date:
        day.clone().add(TREE_WINDOW_DAYS, "days").format("YYYY-MM-DD") + "Z",
      competition: game?.competition?._id,
    });
    results = (results || []).filter(
      (f) => f?.season?._id === game?.season?._id,
    );
    setFixtures(results);
    return results;
  }

  useEffect(() => {
    loadFixtures();
  }, [game?._id]);

  function build(data) {
    let analysis = analyseKnockout(data);
    let tree = buildTree(analysis, overrides);
    return { analysis, tree, resolved: resolveTree(analysis, tree) };
  }

  let view = useMemo(() => build(fixtures), [fixtures, settings]);

  let allTeams = useMemo(
    () =>
      [...view.analysis.teams.values()].sort((a, b) =>
        (a?.name || "").localeCompare(b?.name || ""),
      ),
    [view],
  );

  // Teams from the ties that feed this slot come first, then the rest of this
  // round, then everyone else in the competition.
  function teamOptions(roundIndex, index) {
    let previous = TREE_ROUNDS[roundIndex - 1];
    let feederIds = previous
      ? [
          view.tree[previous.key][2 * index],
          view.tree[previous.key][2 * index + 1],
        ].flatMap((slot) => [slot?.team1, slot?.team2])
      : [];
    let roundIds = (
      view.analysis.rounds[TREE_ROUNDS[roundIndex].key] || []
    ).flatMap((tie) => [tie.team1?._id, tie.team2?._id]);

    let used = new Set();
    let group = (label, ids) => {
      let teams = allTeams.filter(
        (t) => ids.includes(t._id) && !used.has(t._id),
      );
      teams.forEach((t) => used.add(t._id));
      return {
        label,
        options: teams.map((t) => ({ value: t._id, label: t.name })),
      };
    };
    return [
      group("From previous round", feederIds),
      group("In this round", roundIds),
      group(
        "All teams",
        allTeams.map((t) => t._id),
      ),
    ].filter((g) => g.options.length > 0);
  }

  function teamValue(teamId) {
    let team = teamId && view.analysis.teams.get(teamId);
    return team ? { value: team._id, label: team.name } : null;
  }

  // Editing either side pins the whole slot, so the other team stays put
  // when new data arrives.
  function editSlot(roundKey, index, side, teamId) {
    let current = view.tree[roundKey][index] || { team1: null, team2: null };
    setSettings({
      ...settings,
      overrides: {
        ...overrides,
        [slotKey(roundKey, index)]: { ...current, [side]: teamId ?? null },
      },
    });
  }

  function resetSlot(key) {
    let rest = { ...overrides };
    delete rest[key];
    setSettings({ ...settings, overrides: rest });
  }

  async function previewTree() {
    let latest = build(await loadFixtures());
    previewGraphic("TournamentTree", {
      game,
      rounds: TREE_ROUNDS.map((round, r) => ({
        key: round.key,
        visible: r >= startIndex,
        ties: latest.resolved[round.key],
      })),
    });
  }

  let hasKnockoutData = Object.keys(view.analysis.rounds).length > 0;

  return (
    <Panel>
      <PanelTitle>Tournament Tree</PanelTitle>
      <SubTitle>
        {game?.competition?.name}
        {game?.season?.name ? " — " + game?.season?.name : ""}
      </SubTitle>
      <Controls>
        <Field>
          <Label>Start Round</Label>
          <RoundSelect
            styles={DropdownStyles}
            isSearchable={false}
            value={{
              value: TREE_ROUNDS[startIndex].key,
              label: TREE_ROUNDS[startIndex].label,
            }}
            options={TREE_ROUNDS.slice(0, -1).map((round) => ({
              value: round.key,
              label: round.label,
            }))}
            onChange={(e) => {
              setSettings({ ...settings, startRound: e.value });
            }}
          />
        </Field>
        <Button onClick={loadFixtures}>Reload Data</Button>
        <Button
          disabled={Object.keys(overrides).length === 0}
          onClick={() => setSettings({ ...settings, overrides: {} })}
        >
          Reset Edits
        </Button>
        <Button disabled={!fixtures} onClick={previewTree}>
          Preview Tree
        </Button>
      </Controls>
      {!fixtures && <Empty>Loading fixtures…</Empty>}
      {fixtures && !hasKnockoutData && (
        <Empty>
          No knockout fixtures found for this season yet - teams can still be
          picked by hand.
        </Empty>
      )}
      {fixtures && (
        <Tree>
          {TREE_ROUNDS.slice(startIndex).map((round) => {
            let roundIndex = TREE_ROUNDS.indexOf(round);
            return (
              <RoundColumn key={round.key}>
                <RoundTitle>{round.label}</RoundTitle>
                <Ties>
                  {view.tree[round.key].map((slot, index) => {
                    let key = slotKey(round.key, index);
                    let tie = view.resolved[round.key][index];
                    let options = teamOptions(roundIndex, index);
                    return (
                      <TieCard key={key} edited={!!overrides[key]}>
                        {["team1", "team2"].map((side, s) => (
                          <TeamRow key={side}>
                            <TeamSelect
                              styles={DropdownStyles}
                              menuPortalTarget={document.body}
                              isClearable
                              placeholder="TBC"
                              value={teamValue(slot?.[side])}
                              options={options}
                              onChange={(e) => {
                                editSlot(round.key, index, side, e?.value);
                              }}
                            />
                            <Score winner={tie?.winner === s + 1}>
                              {scoreText(tie, s + 1)}
                            </Score>
                          </TeamRow>
                        ))}
                        {overrides[key] && (
                          <ResetSlot onClick={() => resetSlot(key)}>
                            Reset
                          </ResetSlot>
                        )}
                      </TieCard>
                    );
                  })}
                </Ties>
              </RoundColumn>
            );
          })}
        </Tree>
      )}
    </Panel>
  );
}
